import React from 'react';
import { ArrowRight, Building2, Landmark, Store } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import BrowserFrame from './BrowserFrame';
import hotelChatDashboard from '../../assets/erica/hotel-chat-dashboard.png';
import dataPoldaDashboard from '../../assets/erica/data-polda-dashboard.png';
import vendorChatDashboard from '../../assets/erica/vendor-chat-dashboard copy.png';
import { COPY } from '../../i18n/copy';
import { useI18n } from '../../i18n/useI18n';

const ICONS = [Store, Landmark, Building2] as const;
const IMAGES = [hotelChatDashboard, dataPoldaDashboard, vendorChatDashboard] as const;

const EricaUseCases: React.FC = () => {
  const { lang } = useI18n();
  const c = COPY[lang];
  const [activeIndex, setActiveIndex] = React.useState(0);

  const active = c.useCases.highlights[Math.min(activeIndex, c.useCases.highlights.length - 1)];
  const activeImage = IMAGES[Math.min(activeIndex, IMAGES.length - 1)] ?? IMAGES[0];
  const ActiveIcon = ICONS[Math.min(activeIndex, ICONS.length - 1)] ?? Building2;

  return (
    <section id="use-cases" className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            {c.useCases.title}
          </h2>
          <p className="mt-4 text-lg text-slate-600">{c.useCases.subtitle}</p>
        </div>

        <div className="mt-12 lg:hidden">
          <div className="relative">
            <div className="pointer-events-none absolute inset-0 -z-10 rounded-[32px] bg-gradient-to-br from-slate-100 via-white to-white blur-2xl opacity-70" />
            <AnimatePresence mode="wait">
              <motion.div
                key={active.title}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
              >
                <BrowserFrame src={activeImage} alt={active.imageAlt} />
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="mt-4 flex items-center justify-between gap-4">
            <div className="text-xs font-semibold text-slate-500">
              {lang === 'id' ? 'Pilih untuk lihat contoh lain' : 'Tap to explore more examples'}
            </div>
            <button
              type="button"
              onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="inline-flex h-10 items-center justify-center rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition-all hover:from-cyan-500 hover:to-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-700"
            >
              {c.nav.requestDemo}
              <ArrowRight className="ml-2 h-4 w-4" />
            </button>
          </div>

          <div className="-mx-4 mt-4 overflow-x-auto px-4 pb-2">
            <div className="flex w-max gap-2">
              {c.useCases.highlights.map((item, idx) => {
                const Icon = ICONS[Math.min(idx, ICONS.length - 1)] ?? Building2;
                const isActive = idx === activeIndex;
                return (
                  <button
                    key={item.title}
                    type="button"
                    onClick={() => setActiveIndex(idx)}
                    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 ${
                      isActive
                        ? 'border-slate-300 bg-slate-900 text-white'
                        : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50'
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-slate-800'}`} />
                    <span className="whitespace-nowrap">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={`${active.title}-copy`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="mt-6"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50">
                  <ActiveIcon className="h-5 w-5 text-slate-900" />
                </div>
                <div>
                  <div className="text-base font-semibold text-slate-900">{active.title}</div>
                  <div className="text-xs font-semibold text-slate-500">{active.label}</div>
                </div>
              </div>

              <p className="mt-3 text-sm leading-relaxed text-slate-600">{active.description}</p>

              <div className="mt-4 flex flex-wrap gap-2">
                {active.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-12 hidden lg:block">
          <div className="grid gap-4 lg:grid-cols-3">
            {c.useCases.highlights.map((item, idx) => {
              const Icon = ICONS[Math.min(idx, ICONS.length - 1)] ?? Building2;
              const isActive = idx === activeIndex;
              return (
                <button
                  key={item.title}
                  type="button"
                  onClick={() => setActiveIndex(idx)}
                  className={`rounded-2xl border p-4 text-left transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 ${
                    isActive
                      ? 'border-slate-300 bg-slate-900 text-white shadow-sm'
                      : 'border-slate-200 bg-white text-slate-900 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-xl border ${
                        isActive ? 'border-white/30 bg-white/10' : 'border-slate-200 bg-white'
                      }`}
                    >
                      <Icon className={`h-5 w-5 ${isActive ? 'text-white' : 'text-slate-900'}`} />
                    </div>
                    <div className="min-w-0">
                      <div className={`truncate text-base font-semibold ${isActive ? 'text-white' : 'text-slate-900'}`}>
                        {item.title}
                      </div>
                      <div
                        className={`mt-0.5 text-xs font-semibold ${isActive ? 'text-slate-200' : 'text-slate-500'}`}
                      >
                        {item.label}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-6">
            <div className="grid items-start gap-6 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <div className="relative">
                  <div className="pointer-events-none absolute inset-0 -z-10 rounded-[36px] bg-gradient-to-br from-slate-200/80 via-slate-100/40 to-white blur-2xl" />
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={active.title}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3, ease: 'easeOut' }}
                    >
                      <BrowserFrame
                        src={activeImage}
                        alt={active.imageAlt}
                        imageClassName="h-[380px] w-full object-cover object-top xl:h-[420px]"
                      />
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-slate-50">
                    <ActiveIcon className="h-5 w-5 text-slate-900" />
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {lang === 'id' ? 'Ringkasan use case' : 'Use-case snapshot'}
                  </div>
                </div>

                <p className="mt-4 text-base leading-relaxed text-slate-600">{active.description}</p>

                <div className="mt-5 space-y-2 text-sm">
                  <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <span className="font-medium text-slate-600">{lang === 'id' ? 'Industri' : 'Industry'}</span>
                    <span className="font-semibold text-slate-900">{active.label}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <span className="font-medium text-slate-600">{lang === 'id' ? 'Output utama' : 'Primary output'}</span>
                    <span className="font-semibold text-slate-900">
                      {lang === 'id' ? 'Dashboard + insight' : 'Dashboard + insights'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <span className="font-medium text-slate-600">Workflow</span>
                    <span className="font-semibold text-slate-900">Ask → Build → Act</span>
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  {active.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className="mt-6 inline-flex h-11 w-full items-center justify-center rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:from-cyan-500 hover:to-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-700"
                >
                  {c.nav.requestDemo}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EricaUseCases;
